import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types';
import SharepointService from "../../../services/SharepointService";
import Tr from "translations/Translations";
import Dialog from "components/Dialog";
import Loading from "components/Loading";
import Typography from "components/Typography";
import {Breadcrumbs, Checkbox, List, ListItem, ListItemIcon, ListItemText} from "@material-ui/core";
import Link from "@material-ui/core/Link";
import FolderIcon from "@material-ui/icons/Folder";
import GridBlock from "components/GridBlock";
import SubdirectoryArrowLeftIcon from '@material-ui/icons/SubdirectoryArrowLeft';
import {makeStyles} from "@material-ui/styles";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import MultiButton, {createButton} from "components/popup/components/MultiButton";
import SharepointIcon from "components/icon/SharepointIcon";

const useStyle = makeStyles(theme => ({
    root: {},
    rotate: {
        transform: 'rotate(90deg)'
    },
    breadcrumbText: {
        marginRight: theme.spacing(2)
    },
    checkbox: {
        padding: "0 11px"
    },
    selDocuments: {
        marginRight: 200
    },
    dialogContent: {
        minHeight: 500
    },
    loading: {
        position: 'absolute',
        width: 900,
        height: 50
    },
    titleIcon: {
        position: 'absolute',
        left: theme.spacing(2),
        color: theme.palette.primary.main,
    }
}))


function SharepointExplorer(
    {
        demandId,
        onClose,
        handleSubmit
    }) {
    const classes = useStyle();
    const [filesAndFolders, setFilesAndFolders] = useState(null);
    const [selectedFileMap, setSelectedFilesMap] = useState(new Map());
    const [working, setWorking] = useState(true);

    useEffect(() => {
            SharepointService.getFilesAdnFolders(demandId).then((resp) => setFilesAndFolders(resp)).finally(() => setWorking(false));
    }, [demandId])


    const toggleSelectFile = (file) => {
        const newMap = new Map(selectedFileMap);
        if (newMap.has(file.uniqueId)) {
            newMap.delete(file.uniqueId);
            setSelectedFilesMap(new Map(newMap));
        } else {
            newMap.set(file.uniqueId, file)
            setSelectedFilesMap(new Map(newMap));
        }
    }

    const handleBreadcrumbClick = (event) => {
        event.preventDefault();
        const serverRelativeUrl = event.target.getAttribute('data-serverrelativeurl');
        handleClick(serverRelativeUrl);
    }

    const handleClick = (serverRelativeUrl) => {
        if (!working) {
            setWorking(true);
            SharepointService.getFilesAdnFolders(demandId, serverRelativeUrl).then((resp) => {
                if(!!resp)
                    setFilesAndFolders(resp);
            }).finally(() => setWorking(false));
        }
    }

    const folders = useMemo(() => {

        return filesAndFolders ? filesAndFolders.serverRelativeUrl.split("/") : null;
    }, [filesAndFolders]);

    const getButtons = () => {
        const buttons = [];
        buttons.push(createButton(Tr('cancel'), onClose, 'outlined'));
        buttons.push(createButton(Tr('add'), () => handleSubmit(Array.from(selectedFileMap).map(([key, value]) => value)), undefined, selectedFileMap.size === 0));
        return buttons;
    }


    return (
        <Dialog
            open
            title={
            <div>
                <SharepointIcon className={classes.titleIcon} />
                {Tr("int_explorer_dialog_title_x", {x: "sharepoint"}, true)}

            </div>}
            titleCentered
            maxWidth="md"
            onClose={onClose}
            contentClassName={classes.dialogContent}
            dialogActions={
                <>
                    <MultiButton
                        buttons={getButtons()}
                        before={<Typography className={classes.selDocuments}>{`${Tr('documents_selected')}: ${selectedFileMap.size}`} </Typography>}
                    />
                </>
            }
        >

            {working && <Loading className={classes.loading}/>}
            {!!filesAndFolders && <>
                <GridBlock>
                    <Typography className={classes.breadcrumbText}>{Tr('you_are_here')}:</Typography>
                    <Breadcrumbs separator={<NavigateNextIcon fontSize="small"/>} maxItems={3}>

                        {
                            folders.map((elem, index, arr) => {
                                if (index !== arr.length - 1) {
                                    return <Link key={index} href="#" data-serverrelativeurl={arr.slice(0, index + 1).join("/")}
                                                 onClick={handleBreadcrumbClick}>
                                        {index === 0 ? Tr('documents') : elem}
                                    </Link>
                                } else {
                                    return <Typography key={index}>{index === 0 ? Tr('documents') : elem}</Typography>
                                }
                            })
                        }
                    </Breadcrumbs>
                </GridBlock>

                <List dense>
                    {
                        folders.length > 1 && <ListItem button onClick={() => handleClick(folders.slice(0, folders.length - 1).join("/"))}>
                            <ListItemIcon>
                                <SubdirectoryArrowLeftIcon className={classes.rotate}/>
                            </ListItemIcon>
                            <ListItemText
                                primary="[..]"
                            />
                        </ListItem>
                    }


                    {
                        filesAndFolders.folders.map(x => (<ListItem key={x.uniqueId} button onClick={() => handleClick(x.serverRelativeUrl)}>
                            <ListItemIcon>
                                <FolderIcon/>
                            </ListItemIcon>
                            <ListItemText
                                primary={x.name}
                            />
                        </ListItem>))
                    }
                    {
                        filesAndFolders.files.map(x => (<ListItem key={x.uniqueId} disableRipple button onClick={() => toggleSelectFile(x)}>
                            <ListItemIcon>
                                <Checkbox
                                    className={classes.checkbox}
                                    edge="start"
                                    checked={selectedFileMap.has(x.uniqueId)}
                                    tabIndex={-1}
                                    disableRipple
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={x.name}
                            />
                        </ListItem>))
                    }
                    {
                        filesAndFolders.files.length === 0 && filesAndFolders.folders.length === 0 &&
                        <ListItem disabled>
                            <ListItemIcon>
                               <SharepointIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary={Tr('no_files_in_directory')}
                            />
                        </ListItem>
                    }

                </List>
            </>}


        </Dialog>
    )
}

SharepointExplorer.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onClose: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired
}

export default SharepointExplorer;